import React from 'react';
import { Pill, Text } from '@fullstory/components';
import { shortNumber } from '@fullstory/util/fmt';
import { intl } from '@fullstory/intl';
import { getChangeValue } from '../../utils';
import { FormatNumberOptions } from 'react-intl';
import { Color } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';

export interface ChangePillProps
  extends Partial<Pick<React.ComponentProps<typeof Pill>, 'variant'>> {
  value?: number;
  prevValue?: number;
  /**
   * Optionally set percentage number precision.
   *
   * @default Uses a min/max of 1.
   */
  numberFormatOptions?: {
    minimumFractionDigits?: FormatNumberOptions['minimumFractionDigits'];
    maximumFractionDigits?: FormatNumberOptions['maximumFractionDigits'];
    /**
     * Optionally set the number style. Note that if this is set to 'decimal',
     * we calculate the change as the difference between value and prevValue.
     * Otherwise, we compute the `fractionalChange` between the two values.
     */
    style?: FormatNumberOptions['style'];
  };
  /**
   * will flip the negative color to be green, positive to be red
   */
  invertColors?: boolean;
  /**
   * Optionally set the color of the directional change.
   */
  color?: Color;
}

type ColorMap = {
  pill: 'pink-lighter' | 'green-light' | 'secondary';
  text: 'destructive' | 'success' | 'secondary';
};
const COLORS: { neutral: ColorMap; red: ColorMap; green: ColorMap } = {
  neutral: {
    pill: 'secondary',
    text: 'secondary',
  },
  red: {
    pill: 'pink-lighter',
    text: 'destructive',
  },
  green: {
    pill: 'green-light',
    text: 'success',
  },
};

export const ChangePill: React.FC<ChangePillProps> = ({
  value,
  prevValue,
  numberFormatOptions = { minimumFractionDigits: 1, maximumFractionDigits: 1, style: 'percent' },
  invertColors = false,
  variant = 'primary',
  color,
}) => {
  // No change or prevValue is 0 --> gray
  // Negative change --> red
  // Positive change --> green
  const change = getChangeValue(
    value,
    prevValue,
    numberFormatOptions?.style === 'decimal' ? 'decimal' : 'percent',
  );
  const positive = invertColors || color === Color.COLOR_DYNAMIC_RED_UP ? COLORS.red : COLORS.green;
  const negative = invertColors || color === Color.COLOR_DYNAMIC_RED_UP ? COLORS.green : COLORS.red;

  const colorMap = !change ? COLORS.neutral : change > 0 ? positive : negative;

  const pillText =
    change === null
      ? intl.formatMessage({
          defaultMessage: 'N/A',
          id: 'gHLFDB',
          description:
            'Used on a pill indicator to indicate that there is no change between previous and current values.',
        })
      : numberFormatOptions?.style === 'decimal'
      ? shortNumber(change, numberFormatOptions)
      : intl.formatNumber(change, {
          style: 'percent',
          ...numberFormatOptions,
        });

  return (
    <Pill data-component="ChangePill" variant={variant} color={colorMap.pill}>
      <Text appearance="bodySmall" color={colorMap.text} testId="change-pill-text">
        {pillText}
      </Text>
    </Pill>
  );
};
